import { useState } from "react";
import SectionHeader from "@/components/SectionHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { endpoints } from "@/lib/api";
import { Mail, MapPin, Clock, Bug, AlertTriangle, MessageSquare, Lightbulb } from "lucide-react";

const CATEGORIES = [
  { value: "bug", label: "Segnalazione problema tecnico", icon: Bug },
  { value: "data", label: "Dato meteorologico anomalo", icon: AlertTriangle },
  { value: "question", label: "Richiesta informazioni", icon: MessageSquare },
  { value: "suggestion", label: "Suggerimento o proposta", icon: Lightbulb },
];

export default function Contatti() {
  const [form, setForm] = useState({ name: "", email: "", category: "bug", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Compila i campi obbligatori");
      return;
    }
    setSending(true);
    try {
      const cat = CATEGORIES.find((c) => c.value === form.category)?.label ?? "";
      const subject = form.subject ? `[${cat}] ${form.subject}` : `[${cat}]`;
      await endpoints.contact({ ...form, subject });
      toast.success("Segnalazione inviata — ti risponderò appena possibile");
      setForm({ name: "", email: "", category: "bug", subject: "", message: "" });
    } catch {
      toast.error("Impossibile inviare, riprova più tardi");
    } finally { setSending(false); }
  };

  return (
    <div data-testid="page-contatti" className="pt-28 pb-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeader
          eyebrow="Contatti & segnalazioni"
          title="Scrivimi."
          subtitle="Segnalazioni di errori, dati anomali, problemi tecnici o semplici richieste: usa il modulo qui sotto. Le tue segnalazioni mi aiutano a mantenere la stazione affidabile."
        />

        {/* Category picker */}
        <div className="mb-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="category-cards">
          {CATEGORIES.map((c) => {
            const active = form.category === c.value;
            return (
              <button
                key={c.value}
                type="button"
                data-testid={`category-${c.value}`}
                onClick={() => update("category")(c.value)}
                className={`metric-card p-5 text-left flex items-start gap-3 transition-all ${active ? "ring-2 ring-[#0EA5E9] border-transparent" : ""}`}
              >
                <span className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${active ? "bg-[#0EA5E9] text-white" : "bg-[#0B132B]/5 text-[#0B132B]"}`}>
                  <c.icon className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-medium text-sm text-[#0B132B]">{c.label}</p>
                </div>
              </button>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          <form onSubmit={submit} className="lg:col-span-3 metric-card p-6 md:p-10 space-y-5" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-[0.22em] text-slate-500">Nome *</Label>
                <Input id="name" data-testid="contact-name" value={form.name} onChange={update("name")} className="mt-1.5 h-12 rounded-xl" placeholder="Il tuo nome" required />
              </div>
              <div>
                <Label htmlFor="email" className="text-xs uppercase tracking-[0.22em] text-slate-500">Email *</Label>
                <Input id="email" type="email" data-testid="contact-email" value={form.email} onChange={update("email")} className="mt-1.5 h-12 rounded-xl" placeholder="tu@esempio.it" required />
              </div>
            </div>
            <div>
              <Label htmlFor="cat-select" className="text-xs uppercase tracking-[0.22em] text-slate-500">Tipo segnalazione</Label>
              <Select value={form.category} onValueChange={update("category")}>
                <SelectTrigger id="cat-select" data-testid="category-select" className="mt-1.5 h-12 rounded-xl">
                  <SelectValue placeholder="Seleziona tipologia" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="subject" className="text-xs uppercase tracking-[0.22em] text-slate-500">Oggetto</Label>
              <Input id="subject" data-testid="contact-subject" value={form.subject} onChange={update("subject")} className="mt-1.5 h-12 rounded-xl" placeholder="Breve descrizione" />
            </div>
            <div>
              <Label htmlFor="message" className="text-xs uppercase tracking-[0.22em] text-slate-500">Descrizione *</Label>
              <Textarea id="message" data-testid="contact-message" value={form.message} onChange={update("message")} className="mt-1.5 rounded-xl min-h-40" placeholder="Racconta cosa hai notato: pagina, orario, valore mostrato, dispositivo utilizzato…" required />
            </div>
            <Button type="submit" data-testid="contact-submit" disabled={sending} className="rounded-full h-12 px-8 bg-[#0B132B] hover:bg-[#0EA5E9]">
              {sending ? "Invio in corso…" : "Invia segnalazione"}
            </Button>
          </form>

          <div className="lg:col-span-2 space-y-6">
            <div className="metric-card p-6 md:p-8 space-y-5">
              <InfoRow icon={MapPin} label="Sede stazione" value="Via Loreto, 00036 Palestrina (RM)" />
              <InfoRow icon={Mail} label="Email" value="meteovialoreto@gmail.com" />
              <InfoRow icon={Clock} label="Disponibilità dati" value="24/7 · aggiornamento ogni 60 s" />
            </div>
            <div className="rounded-3xl overflow-hidden shadow-lg border border-slate-200 aspect-square" data-testid="contact-map">
              <iframe
                title="Mappa Palestrina Via Loreto"
                className="w-full h-full"
                src="https://www.openstreetmap.org/export/embed.html?bbox=12.87%2C41.815%2C12.91%2C41.836&layer=mapnik&marker=41.825544%2C12.889484"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-4">
      <span className="h-11 w-11 rounded-full bg-[#0B132B] text-[#FBBF24] flex items-center justify-center flex-shrink-0">
        <Icon className="h-4 w-4" />
      </span>
      <div>
        <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500">{label}</p>
        <p className="font-medium text-[#0B132B] mt-0.5">{value}</p>
      </div>
    </div>
  );
}
